import {SelectionModel} from '@angular/cdk/collections';
import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, signal, input, output } from '@angular/core';
import {Entity, EntityKey} from '../../entity/entity';
import {OrderService} from '../../services/order-service';
import {StringUtils} from '../../utils/string-utils';
import {ReleaseSingleEntityLine} from './release-single-entity-line';
import {ModReleaseFilter} from './mod-release-filter';
import {MatDialog} from '@angular/material/dialog';
import {ModReleaseHelpDialogComponent} from '../../dialogs/mod-release-help-dialog/mod-release-help-dialog.component';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource} from '@angular/material/table';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {FormsModule} from '@angular/forms';
import {MatInput} from '@angular/material/input';
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';


@Component({
    selector: 'mod-release',
    templateUrl: './mod-release.component.html',
    styleUrls: ['./mod-release.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressSpinner, MatButton, MatIcon, MatFormField, MatLabel, MatInput, FormsModule, MatSelect, MatOption, MatPaginator, MatCheckbox, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatSortHeader, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow]
})
export class ModReleaseComponent implements OnInit {
  private readonly orderService = inject(OrderService);
  private readonly dialog = inject(MatDialog);


  readonly releaseGenerated = output<Entity>();
  readonly entityKey = input.required<EntityKey>();
  @ViewChild(MatSort, {static: true}) sort!: MatSort;
  @ViewChild('paginator', {read: MatPaginator, static: true}) paginator!: MatPaginator;
  foDate!: Date;
  riserDate!: Date;
  thermostatDate!: Date;
  unitDate!: Date;
  readonly selection = signal<SelectionModel<ReleaseSingleEntityLine>>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  releaseQuantity: number = 0;
  datasource!: MatTableDataSource<ReleaseSingleEntityLine>;
  descriptionFilter!: string;
  readonly buildingValues = signal<string[]>(undefined!);
  buildingFilters!: string[];
  readonly floorValues = signal<string[]>(undefined!);
  floorFilters!: string[];
  readonly riserValues = signal<string[]>(undefined!);
  riserFilters!: string[];
  readonly roomValues = signal<string[]>(undefined!);
  roomFilters!: string[];
  readonly tagValues = signal<string[]>(undefined!);
  tagFilters!: string[];
  unreleasedOnly!: boolean;
  displayedColumns: string[] = ['select', 'lineId', 'description', 'building', 'floor', 'riser', 'room', 'tag',
    'unitReleaseDate', 'unitErpOrderNumber', 'riserReleaseDate', 'riserErpOrderNumber',
    'foReleaseDate', 'foErpOrderNumber', 'thermostatReleaseDate', 'thermostatErpOrderNumber'];

  ngOnInit(): void {
    this.resetStatus();
    this.foDate = new Date();
    this.riserDate = new Date();
    this.thermostatDate = new Date();
    this.unitDate = new Date();
    this.orderService.getModReleaseUnits(this.entityKey()).subscribe(results => {
      this.isBusy.set(false);
      this.buildingValues.set([...new Set(results.map(result => result.building).sort())]);
      this.floorValues.set([...new Set(results.map(result => result.floor).sort())]);
      this.riserValues.set([...new Set(results.map(result => result.riser).sort())]);
      this.roomValues.set([...new Set(results.map(result => result.room).sort())]);
      this.tagValues.set([...new Set(results.map(result => result.tag).sort())]);
      this.selection.set(new SelectionModel<ReleaseSingleEntityLine>(true, []));
      this.datasource = new MatTableDataSource<ReleaseSingleEntityLine>(results);
      this.datasource.sort = this.sort;
      this.datasource.paginator = this.paginator;
      this.datasource.filterPredicate = this.createFilterPredicate();
    }, error => this.handleError(error));
  }

  private hasValue(input: string): boolean {
    return !!(input && input.length > 0)
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
  }

  generateRelease(): void {
    this.resetStatus();
    let units: ReleaseSingleEntityLine[] = this.buildUnitsList();
    if (units.length === 0) {
      this.errorMessage.set("No lines were selected to release");
      this.isBusy.set(false);
    } else {
      this.orderService.releaseModUnits(this.entityKey(), units).subscribe(result => {
        this.releaseGenerated.emit(result);
      }, error => this.handleError(error));
    }
  }

  private createFilterPredicate(): (data: ReleaseSingleEntityLine, filter: string) => boolean {
    let filterFunction = function (data: ReleaseSingleEntityLine, filter: string): boolean {
      let releaseFilter: ModReleaseFilter = JSON.parse(filter);
      if (releaseFilter.unreleasedOnly && data.released) {
        return false;
      }
      let isLineInFilter = function (property: string, selections: string[]): boolean {
        if (!selections || selections.length === 0) {
          return true;
        } else if (!property || property.length == 0) {
          return selections.filter(selection => !selection || selection.length === 0).length > 0;
        } else {
          return selections.filter(selection => property.toLowerCase() == selection.toLowerCase()).length > 0;
        }
      }
      let descriptionMatches = function (property: string, filterText: string): boolean {
        if (!filterText || filterText.trim().length === 0) {
          return true;
        } else if (!property || property.trim().length === 0) {
          return false;
        } else {
          let matchProperty: string = property.toLowerCase().replace(/\_/g, '');
          let searchProperty: string = filterText.trim().toLowerCase().replace(/\_/g, '');
          return matchProperty.indexOf(searchProperty) >= 0;
        }
      }
      if (!descriptionMatches(data.description, releaseFilter.description)) return false;
      if (!isLineInFilter(data.building, releaseFilter.building)) return false;
      if (!isLineInFilter(data.floor, releaseFilter.floor)) return false;
      if (!isLineInFilter(data.riser, releaseFilter.riser)) return false;
      if (!isLineInFilter(data.room, releaseFilter.room)) return false;
      if (!isLineInFilter(data.tag, releaseFilter.tag)) return false;
      return true;
    }
    return filterFunction;
  }

  filterList(): void {
    let releaseFilter: ModReleaseFilter = new ModReleaseFilter();
    releaseFilter.description = this.descriptionFilter;
    releaseFilter.building = this.buildingFilters;
    releaseFilter.floor = this.floorFilters;
    releaseFilter.riser = this.riserFilters;
    releaseFilter.room = this.roomFilters;
    releaseFilter.tag = this.tagFilters;
    releaseFilter.unreleasedOnly = this.unreleasedOnly;
    this.datasource.filter = JSON.stringify(releaseFilter);
  }

  private buildUnitsList(): ReleaseSingleEntityLine[] {
    let selectedLines: ReleaseSingleEntityLine[] = this.selection().selected;
    for (let line of selectedLines) {
      line.foReleaseDate = this.foDate.toISOString().substring(0, 10);
      line.riserReleaseDate = this.riserDate.toISOString().substring(0, 10);
      line.thermostatReleaseDate = this.thermostatDate.toISOString().substring(0, 10);
      line.unitReleaseDate = this.unitDate.toISOString().substring(0, 10);
    }
    return selectedLines;
  }

  toggleRowSelection(row: ReleaseSingleEntityLine): void {
    if (!row.released) {
      this.selection().toggle(row);
      this.updateReleaseQuantity();
    }
  }

  private updateReleaseQuantity(): void {
    if (this.selection().selected && this.selection().selected.length > 0) {
      this.releaseQuantity = this.selection().selected.length;
    } else {
      this.releaseQuantity = 0;
    }
  }

  masterToggle(): void {
    this.isAllSelected() ?
      this.datasource.filteredData.filter(row => !row.released).forEach(row => this.selection().deselect(row)) :
      this.datasource.filteredData.filter(row => !row.released).forEach(row => this.selection().select(row));
    this.updateReleaseQuantity();
  }

  isAllSelected(): boolean {
    if (this.selection() && this.datasource && this.datasource.filteredData) {
      let available: ReleaseSingleEntityLine[] = this.datasource.filteredData.filter(row => !row.released);
      return this.selection().selected && (this.selection().selected.filter(entry => this.availableListContainsEntry(available, entry)).length === available.length);
    } else {
      return false;
    }
  }

  private availableListContainsEntry(available: ReleaseSingleEntityLine[], entry: ReleaseSingleEntityLine): boolean {
    return entry && available && available.length > 0 && available.filter(filtered => {
      return filtered.lineId == entry.lineId && filtered.sequence == entry.sequence;
    }).length === 1;
  }

  public openHelpDialog(): void {
    this.dialog.open(ModReleaseHelpDialogComponent, {
      width: '1100px',
      height: '730px',
    });
  }

}
